import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Paper,
  Group,
  Text,
  TextInput,
  Button,
  ActionIcon,
  Modal,
  Stack,
  Select,
  Tabs,
  Badge,
  Menu,
  Flex,
} from '@mantine/core';
import {
  IconPlus,
  IconFilter,
  IconSearch,
  IconChevronDown,
  IconLayoutGrid,
  IconList,
  IconColumns,
  IconCalendar,
  IconSettings,
  IconDownload,
} from '@tabler/icons-react';
import { BaseSurface, SurfaceProps } from './SurfaceBase';
import { KanbanView, ListView, GalleryView, CalendarView } from '../views';
import { PropertyManager } from '../properties';
import { Database, DatabaseItem, createDatabase, createDatabaseItem } from '../../../types/models/Database';
import { loadDatabase, saveDatabase } from '../../../core/workspace/databaseStorage';
import { eventBus } from '../../../core/event-bus';

interface DatabaseViewProps extends SurfaceProps {
  database?: Database;
  onDatabaseChange?: (database: Database) => void;
}

/**
 * DatabaseView Component
 * Complete database surface with Gallery, Kanban, List, and Calendar views
 * Uses Core Data Models (Database, DatabaseItem, Property)
 */
export const DatabaseView: React.FC<DatabaseViewProps> = ({
  id = 'database-view',
  phase = 10,
  title = 'Database',
  description = 'Manage your database with multiple views',
  isActive = true,
  isLoading = false,
  error,
  className = '',
  database,
  onDatabaseChange,
}) => {
  // Load database from storage by id (which is the pageId passed in).
  // If the prop is provided, use that. Otherwise load from localStorage.
  const [db, setDb] = useState<Database>(() => {
    if (database) return database;
    const stored = loadDatabase(id);
    return stored || createDatabase(id, title || 'My Database');
  });

  // Persist to storage whenever the database changes
  useEffect(() => {
    if (!database) {
      // Only persist if we're managing state ourselves (i.e. loaded from storage)
      try {
        saveDatabase(db);
      } catch {}
    }
    onDatabaseChange?.(db);
  }, [db, database, onDatabaseChange]);

  // Listen for external 'database:refresh' events (e.g. after a seed populates items
  // in localStorage while this component is already mounted). Without this, the
  // useState lazy initializer would have loaded the empty db at mount time and
  // subsequent saves would overwrite the freshly-seeded items.
  useEffect(() => {
    const handler = (data: any) => {
      if (!data || data.databaseId !== id) return;
      const fresh = loadDatabase(id);
      if (fresh) {
        setDb(fresh);
      }
    };
    eventBus.on('database:refresh', handler);
    return () => eventBus.off('database:refresh', handler);
  }, [id]);

  const [viewMode, setViewMode] = useState<'gallery' | 'kanban' | 'list' | 'calendar'>('kanban');
  const [globalSearch, setGlobalSearch] = useState('');
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const [isNewItemOpen, setIsNewItemOpen] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [groupByProperty, setGroupByProperty] = useState<string | undefined>('date');

  // Load view preference from localStorage
  useEffect(() => {
    const savedView = localStorage.getItem(`db-view-preference-${db.id}`);
    if (savedView && ['gallery', 'kanban', 'list', 'calendar'].includes(savedView)) {
      setViewMode(savedView as any);
    }
  }, [db.id]);

  // Save view preference to localStorage
  const handleViewModeChange = useCallback((newMode: 'gallery' | 'kanban' | 'list' | 'calendar') => {
    setViewMode(newMode);
    localStorage.setItem(`db-view-preference-${db.id}`, newMode);
  }, [db.id]);

  // Handle database changes
  const handleDatabaseChange = useCallback((updatedDb: Database) => {
    setDb(updatedDb);
    onDatabaseChange?.(updatedDb);
  }, [onDatabaseChange]);

  // Filter items
  const filteredItems = useMemo(() => {
    if (!globalSearch) return db.items;

    const searchLower = globalSearch.toLowerCase();
    return db.items.filter(item =>
      item.title.toLowerCase().includes(searchLower) ||
      item.properties.some(prop =>
        String(prop.value).toLowerCase().includes(searchLower)
      )
    );
  }, [db.items, globalSearch]);

  // Handle add item
  const handleAddItem = () => {
    if (!newItemTitle.trim()) return;

    const newItem = createDatabaseItem(
      `item_${Date.now()}`,
      db.id,
      newItemTitle
    );

    if (groupByProperty) {
      newItem.properties.push({
        propertyId: groupByProperty,
        value: undefined,
      });
    }

    const updatedDb = {
      ...db,
      items: [...db.items, newItem],
      updatedAt: new Date(),
    };

    handleDatabaseChange(updatedDb);
    setIsNewItemOpen(false);
    setNewItemTitle('');
  };

  // Handle update item
  const handleUpdateItem = (item: DatabaseItem) => {
    const updatedDb = {
      ...db,
      items: db.items.map(i => (i.id === item.id ? item : i)),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
  };

  // Handle delete item
  const handleDeleteItem = (itemId: string) => {
    const updatedDb = {
      ...db,
      items: db.items.filter(i => i.id !== itemId),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
  };

  // Handle duplicate item
  const handleDuplicateItem = (item: DatabaseItem) => {
    const newItem: DatabaseItem = {
      ...item,
      id: `item_${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const updatedDb = {
      ...db,
      items: [...db.items, newItem],
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
  };

  // Handle properties change
  const handlePropertiesChange = (properties: any[]) => {
    const updatedDb = {
      ...db,
      schema: {
        ...db.schema,
        properties,
      },
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
  };

  // Handle export
  const handleExport = () => {
    const dataStr = JSON.stringify(db, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `database_${db.id}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <BaseSurface
      id={id}
      phase={phase}
      title={title}
      description={description}
      isActive={isActive}
      isLoading={isLoading}
      error={error || ''}
      className={`database-view-surface-wrapper ${className}`}
    >
      <div className="database-view-content" style={{ height: '100%' }}>
        {/* Toolbar */}
        <Paper p="md" mb="md" radius="md" withBorder>
          <Group justify="space-between" align="center" wrap="wrap">
            <Group gap="sm">
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => setIsNewItemOpen(true)}
              >
                New Item
              </Button>
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button
                    variant="subtle"
                    rightSection={<IconChevronDown size={14} />}
                  >
                    View
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconLayoutGrid size={16} />}
                    onClick={() => handleViewModeChange('gallery')}
                  >
                    Gallery
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconColumns size={16} />}
                    onClick={() => handleViewModeChange('kanban')}
                  >
                    Kanban
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconList size={16} />}
                    onClick={() => handleViewModeChange('list')}
                  >
                    List
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCalendar size={16} />}
                    onClick={() => handleViewModeChange('calendar')}
                  >
                    Calendar
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>

            <TextInput
              placeholder="Search items..."
              leftSection={<IconSearch size={16} />}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.currentTarget.value)}
              style={{ flex: 1, maxWidth: '300px' }}
            />

            <Group gap="sm">
              <Badge variant="light">
                {filteredItems.length} items
              </Badge>
              <Button
                leftSection={<IconDownload size={16} />}
                variant="light"
                size="sm"
                onClick={handleExport}
              >
                Export
              </Button>
              <Button
                leftSection={<IconSettings size={16} />}
                variant="light"
                size="sm"
                onClick={() => setIsPropertiesOpen(true)}
              >
                Properties
              </Button>
            </Group>
          </Group>
        </Paper>

        {/* Active View Indicator */}
        <Group justify="flex-start" mb="sm" gap="xs">
          {viewMode === 'gallery' && <Badge>Gallery View</Badge>}
          {viewMode === 'kanban' && <Badge>Kanban View</Badge>}
          {viewMode === 'list' && <Badge>List View</Badge>}
          {viewMode === 'calendar' && <Badge>Calendar View</Badge>}
        </Group>

        {/* Views */}
        <div style={{ width: '100%' }}>
          {viewMode === 'gallery' && (
            <GalleryView
              database={db}
              items={filteredItems}
              onItemSelect={console.log}
              onItemCreate={handleAddItem}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
            />
          )}

          {viewMode === 'kanban' && (
            <KanbanView
              database={db}
              items={filteredItems}
              groupByPropertyId={groupByProperty}
              onItemSelect={console.log}
              onItemCreate={handleAddItem}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
            />
          )}

          {viewMode === 'list' && (
            <ListView
              database={db}
              items={filteredItems}
              onItemSelect={console.log}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
            />
          )}

          {viewMode === 'calendar' && (
            <CalendarView
              database={db}
              items={filteredItems}
              datePropertyId={
                db.schema.properties.find(p => p.type === 'date')?.id
              }
              onItemSelect={console.log}
            />
          )}
        </div>

        {/* New Item Modal */}
        <Modal
          opened={isNewItemOpen}
          onClose={() => {
            setIsNewItemOpen(false);
            setNewItemTitle('');
          }}
          title="Create New Item"
          size="sm"
        >
          <Stack>
            <TextInput
              label="Item Title"
              placeholder="Enter item title"
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddItem();
              }}
              autoFocus
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                onClick={() => {
                  setIsNewItemOpen(false);
                  setNewItemTitle('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleAddItem} disabled={!newItemTitle.trim()}>
                Create
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Properties Modal */}
        <Modal
          opened={isPropertiesOpen}
          onClose={() => setIsPropertiesOpen(false)}
          title="Database Properties"
          size="md"
          scrollAreaComponent={Paper}
        >
          <PropertyManager
            properties={db.schema.properties}
            onPropertiesChange={handlePropertiesChange}
          />

          {/* Kanban grouping option */}
          <Paper p="md" mt="lg" radius="md" withBorder>
            <Text fw={500} mb="xs">
              Kanban Settings
            </Text>
            <Select
              label="Group by property"
              placeholder="Select a property"
              data={[
                { value: '', label: 'None' },
                ...db.schema.properties
                  .filter(p => p.type === 'select' || p.type === 'status')
                  .map(p => ({ value: p.id, label: p.name })),
              ]}
              value={groupByProperty || ''}
              onChange={(val) => setGroupByProperty(val || undefined)}
            />
          </Paper>
        </Modal>
      </div>
    </BaseSurface>
  );
};

export default DatabaseView;
